import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import OrderService from '../services/orderService';
import CustomerService from '../services/customerService';
import { Order, Customer } from '../types';
// Removed Ant Design time picker
import moment from 'moment';
import { formatUTCToLocal, formatLocalToUTC, formatDateForInput, formatTimeForInput, createUTCDatetime } from '../utils/timezoneUtils';
import LogService from '../services/logService';
import AuthService from '../services/authService';

const OrderForm: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const currentUser = AuthService.getCurrentUser();
    const userPrefix = currentUser?.role === 'staff' ? '/staff' : '/admin';
    const isEditMode = !!id;

    // State để theo dõi kích thước màn hình
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;

    // CSS cho responsive buttons
    const buttonContainerStyle: React.CSSProperties = {
        gap: '10px',
        justifyContent: isMobile ? 'center' : 'flex-end',
        paddingBottom: '90px' // chừa chỗ cho FooterNavigation cố định
    };

    // Lắng nghe sự thay đổi kích thước màn hình
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Cập nhật meta viewport cho thiết bị di động để ngăn hiệu ứng zoom không mong muốn
    useEffect(() => {
        // Tìm meta viewport tag hiện có
        let metaViewport = document.querySelector('meta[name="viewport"]');

        // Nếu không tìm thấy, tạo mới
        if (!metaViewport) {
            metaViewport = document.createElement('meta');
            metaViewport.setAttribute('name', 'viewport');
            document.head.appendChild(metaViewport);
        }

        // Cập nhật nội dung meta viewport để ngăn chặn zoom
        metaViewport.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no');

        // Cleanup khi component unmount
        return () => {
            // Khôi phục lại giá trị ban đầu khi component unmount
            metaViewport?.setAttribute('content', 'width=device-width, initial-scale=1.0');
        };
    }, []);

    const [order, setOrder] = useState<Order>({
        customerId: 0,
        product: '',
        quantity: 1,
        price: 0,
        totalValue: 0,
        deposit: 0,
        remainingAmount: 0,
        shippingFee: 0,
        deliveryDate: new Date().toISOString().split('T')[0],
        status: 'pending',
        address: '',
        notes: '',
        remarks: '',
    });

    // State để lưu trữ giá trị định dạng hiển thị cho các trường tiền tệ (có dấu phân cách hàng nghìn)
    const [formattedValues, setFormattedValues] = useState({
        price: '',
        totalValue: '',
        deposit: '',
        shippingFee: '',
        remainingAmount: ''
    });

    const [originalOrder, setOriginalOrder] = useState<Order | null>(null);
    const [customer, setCustomer] = useState<Customer | null>(null);
    const [phoneNumber, setPhoneNumber] = useState<string>('');
    const [customerName, setCustomerName] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [phoneSearchTimeout, setPhoneSearchTimeout] = useState<NodeJS.Timeout | null>(null);
    const [isNewCustomer, setIsNewCustomer] = useState<boolean>(!isEditMode);
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [deliveryTime, setDeliveryTime] = useState<moment.Moment | null>(moment.tz('Asia/Ho_Chi_Minh'));
    const [dateDisplay, setDateDisplay] = useState<string>('');
    const [timeDisplay, setTimeDisplay] = useState<string>('');

    useEffect(() => {
        if (isEditMode) {
            loadOrder();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    useEffect(() => {
        // Cập nhật totalValue khi quantity hoặc price thay đổi
        setOrder(prev => ({
            ...prev,
            totalValue: prev.quantity * prev.price
        }));
    }, [order.quantity, order.price]);

    useEffect(() => {
        // Cập nhật remainingAmount khi totalValue, deposit hoặc shippingFee thay đổi
        setOrder(prev => {
            const total = prev.totalValue || 0;
            const shipping = prev.shippingFee || 0;
            const dep = prev.deposit || 0;
            const remaining = total + shipping - dep;

            // Log để debug
            // console.log('Cập nhật remainingAmount:', {
            // total,
            //  shipping,
            //  dep,
            //  remaining
            // });

            return {
                ...prev,
                remainingAmount: remaining
            };
        });
    }, [order.totalValue, order.deposit, order.shippingFee]);

    // Thêm effect để cập nhật các giá trị định dạng khi order thay đổi
    useEffect(() => {
        setFormattedValues({
            price: formatNumberInput(order.price),
            totalValue: formatNumberInput(order.totalValue),
            deposit: formatNumberInput(order.deposit),
            shippingFee: formatNumberInput(order.shippingFee),
            remainingAmount: formatNumberInput(order.remainingAmount)
        });
    }, [order.price, order.totalValue, order.deposit, order.shippingFee, order.remainingAmount]);

    /**
     * Định dạng số với dấu phân cách hàng nghìn theo chuẩn Việt Nam
     * @param value - Số cần định dạng
     * @returns Chuỗi đã định dạng (VD: 1000000 -> "1.000.000")
     */
    const formatNumberInput = (value: number): string => {
        return value.toLocaleString('vi-VN');
    };

    /**
     * Chuyển đổi chuỗi định dạng thành số nguyên
     * @param value - Chuỗi có định dạng (VD: "1.000.000")
     * @returns Số nguyên (VD: 1000000)
     */
    const parseFormattedNumber = (value: string): number => {
        // Loại bỏ tất cả các ký tự không phải số
        const numericString = value.replace(/[^\d]/g, '');
        return numericString ? parseInt(numericString, 10) : 0;
    };

    const loadOrder = async () => {
        try {
            setLoading(true);
            const response = await OrderService.getById(Number(id));

            // Log dữ liệu nhận được từ server
            //console.log('Dữ liệu đơn hàng từ server:', response.data);
            // console.log('Ngày giao hàng từ server:', response.data.deliveryDate);

            // Xử lý định dạng ngày giao hàng cho input type date
            const orderData = { ...response.data } as Order & { [key: string]: any };
            if (orderData.deliveryDate) {
                // Sử dụng utility function để chuyển đổi UTC sang local
                const localDate = formatDateForInput(orderData.deliveryDate);
                const localTimeStr = formatTimeForInput(orderData.deliveryDate);
                
                if (localDate) {
                    orderData.deliveryDate = localDate;
                    setDateDisplay(formatUTCToLocal(orderData.deliveryDate, 'DD/MM/YYYY'));
                    
                    // Chuyển đổi string thành moment object
                    if (localTimeStr) {
                        const localTimeMoment = moment(localTimeStr, 'HH:mm', true);
                        if (localTimeMoment.isValid()) {
                            setDeliveryTime(localTimeMoment);
                        } else {
                            setDeliveryTime(moment().tz('Asia/Ho_Chi_Minh'));
                        }
                    } else {
                        setDeliveryTime(moment().tz('Asia/Ho_Chi_Minh'));
                    }
                } else {
                    console.warn('Ngày không hợp lệ:', orderData.deliveryDate);
                }
            }

            // Đảm bảo các trường số được xử lý đúng kiểu
            const numericFields = ['quantity', 'price', 'totalValue', 'deposit', 'shippingFee', 'remainingAmount'];
            numericFields.forEach(field => {
                if (orderData[field] !== undefined) {
                    orderData[field] = Number(orderData[field]);
                    console.log(`Chuyển đổi trường ${field}:`, orderData[field]);
                }
            });

            setOrder(orderData);
            // Lưu trữ dữ liệu gốc để có thể đặt lại form
            setOriginalOrder(orderData);

            // Nếu đơn hàng có hình ảnh
            if (response.data.imagePath) {
                const imagePath = response.data.imagePath;
                const apiUrl = 'https://api.hoaithuongfruits.com';
                const imageUrl = `${apiUrl}${imagePath}`;
                console.log('Đường dẫn hình ảnh gốc:', imagePath);
                console.log('API URL:', apiUrl);
                console.log('Đường dẫn hình ảnh đầy đủ:', imageUrl);
                setImagePreview(imageUrl);

                // Thử tạo request test để kiểm tra hình ảnh
                fetch(imageUrl)
                    .then(response => {
                        console.log('Kết quả kiểm tra hình ảnh:', response.status, response.statusText);
                        if (!response.ok) {
                            console.error('Không thể tải hình ảnh từ URL:', imageUrl);
                        }
                    })
                    .catch(error => {
                        console.error('Lỗi khi tải hình ảnh:', error);
                    });
            }

            // Xử lý thời gian giao hàng
            if (response.data.deliveryDate) {
                try {
                    // Lấy thời gian theo UTC và convert về Asia/Ho_Chi_Minh
                    const dateTime = moment.utc(response.data.deliveryDate).tz('Asia/Ho_Chi_Minh');
                    if (dateTime.isValid()) {
                        console.log('Đặt thời gian giao hàng:', dateTime.format('YYYY-MM-DD HH:mm:ss'));
                        setDeliveryTime(dateTime);
                        setTimeDisplay(dateTime.format('HH:mm'));
                    } else {
                        console.warn('Không thể đặt thời gian giao hàng, định dạng không hợp lệ');
                        const now = moment();
                        setDeliveryTime(now);
                        setTimeDisplay(now.format('HH:mm'));
                    }
                } catch (error) {
                    console.error('Lỗi khi xử lý thời gian giao hàng:', error);
                    const now = moment();
                    setDeliveryTime(now);
                    setTimeDisplay(now.format('HH:mm'));
                }
            }

            // Nếu đơn hàng có khách hàng
            if (response.data.customerId) {
                try {
                    const customerResponse = await CustomerService.getById(response.data.customerId);
                    setCustomer(customerResponse.data);
                    setPhoneNumber(customerResponse.data.phoneNumber);
                    setCustomerName(customerResponse.data.name || '');
                } catch (error) {
                    console.error('Lỗi khi tải thông tin khách hàng:', error);
                    // Nếu không lấy được thông tin khách hàng từ ID, sử dụng customerName từ đơn hàng
                    if (response.data.customerName) {
                        setCustomerName(response.data.customerName);
                    }
                }
            } else if (response.data.customerName) {
                // Nếu đơn hàng không có customerId nhưng có customerName
                setCustomerName(response.data.customerName);
            }

            setError(null);
        } catch (err) {
            setError('Không thể tải thông tin đơn hàng. Vui lòng thử lại sau.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    // Đồng bộ hiển thị ngày/giờ khi giá trị nội bộ thay đổi (trường hợp tạo mới)
    useEffect(() => {
        if (order.deliveryDate) {
            const d = moment(order.deliveryDate, 'YYYY-MM-DD', true);
            if (d.isValid()) {
                setDateDisplay(d.format('DD/MM/YYYY'));
            }
        } else {
            setDateDisplay('');
        }
    }, [order.deliveryDate]);

    useEffect(() => {
        if (deliveryTime && deliveryTime.isValid()) {
            setTimeDisplay(deliveryTime.format('HH:mm'));
        }
    }, [deliveryTime]);

    const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setPhoneNumber(value);

        // Xóa timeout cũ nếu có
        if (phoneSearchTimeout) {
            clearTimeout(phoneSearchTimeout);
        }

        // Nếu người dùng xóa số điện thoại, đặt trạng thái là khách hàng mới
        if (!value.trim()) {
            setCustomer(null);
            setIsNewCustomer(true);
            return;
        }

        // Tạo timeout mới để tránh gọi API liên tục khi người dùng đang nhập
        if (value.trim().length >= 3) {
            const timeout = setTimeout(() => {
                searchCustomerByPhone(value);
            }, 500); // Đợi 500ms sau khi người dùng ngừng gõ

            setPhoneSearchTimeout(timeout);
        }
    };

    const searchCustomerByPhone = async (phone: string) => {
        if (!phone.trim()) return;

        try {
            // Không hiển thị loading khi tìm kiếm số điện thoại
            const response = await CustomerService.findByPhone(phone);

            if (response.data && (Array.isArray(response.data) ? response.data.length > 0 : response.data.id)) {
                const foundCustomer = Array.isArray(response.data) ? response.data[0] : response.data;
                setCustomer(foundCustomer);
                setCustomerName(foundCustomer.name || '');
                setOrder(prev => ({
                    ...prev,
                    customerId: foundCustomer.id || 0,
                    address: foundCustomer.address || ''
                }));
                setIsNewCustomer(false);
                setError(null);
            } else {
                // Không tìm thấy khách hàng, cho phép tạo mới
                setCustomer(null);
                setCustomerName('');
                setIsNewCustomer(true);
            }
        } catch (err) {
            console.error(err);
        } finally {
            // Không cần reset loading
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;

        // Kiểm tra nếu đang thay đổi trạng thái đơn hàng
        if (name === 'status' && isEditMode && originalOrder) {
            const oldStatus = originalOrder.status;
            const newStatus = value as 'pending' | 'preparing' | 'completed' | 'cancelled' | 'done';

            // Nếu có sự thay đổi trạng thái
            if (oldStatus !== newStatus) {
                // Tạo log cho việc thay đổi trạng thái
                try {
                    const currentUser = AuthService.getCurrentUser();
                    if (currentUser) {
                        const getStatusText = (status: string) => {
                            switch (status) {
                                case 'pending': return 'Chờ xử lý';
                                case 'preparing': return 'Đang xử lý';
                                case 'completed': return 'Hoàn thành';
                                case 'done': return 'Sẵn sàng giao';
                                case 'cancelled': return 'Đã hủy';
                                default: return 'Không xác định';
                            }
                        };

                        const logData = {
                            action: 'updated' as 'created' | 'updated' | 'deleted',
                            resourceType: 'order' as 'order' | 'customer' | 'staff',
                            resourceId: Number(id),
                            details: `Cập nhật trạng thái đơn hàng "${order.product}" từ ${getStatusText(oldStatus)} sang ${getStatusText(newStatus)}`,
                            performedById: currentUser.id as number
                        };

                        LogService.create(logData)
                            .then(() => console.log('Đã tạo log cho thay đổi trạng thái'))
                            .catch(err => console.error('Lỗi khi tạo log:', err));
                    }
                } catch (err) {
                    console.error('Lỗi khi xử lý tạo log:', err);
                }
            }
        }

        // Xử lý các trường tiền tệ với định dạng số
        if (name === 'price' || name === 'deposit' || name === 'shippingFee') {
            const numericValue = parseFormattedNumber(value);

            // Ngăn chặn giá trị âm
            if (numericValue < 0) {
                return;
            }

            // Cập nhật giá trị vào state order
            setOrder(prev => ({
                ...prev,
                [name]: numericValue
            }));

            // Cập nhật giá trị định dạng
            setFormattedValues(prev => ({
                ...prev,
                [name]: value
            }));
        }
        // Kiểm tra và xử lý giá trị số cho quantity
        else if (name === 'quantity') {
            const numericValue = value === '' ? 0 : parseFloat(value);

            // Ngăn chặn giá trị âm
            if (numericValue < 0) {
                return;
            }

            setOrder(prev => ({
                ...prev,
                [name]: numericValue
            }));
        }
        // Xử lý các trường thông thường
        else {
            setOrder(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleCustomerNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setCustomerName(e.target.value);
    };

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setImageFile(file);

            // Tạo URL preview
            const reader = new FileReader();
            reader.onload = (event) => {
                if (event.target?.result) {
                    setImagePreview(event.target.result as string);
                }
            };
            reader.readAsDataURL(file);
        }
    };

    const handleRemoveImage = () => {
        setImageFile(null);
        setImagePreview(null);
        // Reset file input
        const fileInput = document.getElementById('image') as HTMLInputElement;
        if (fileInput) {
            fileInput.value = '';
        }
    };

    const handleTimeInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value; // HH:mm
        setTimeDisplay(value);
        if (value) {
            const newTime = moment(value, 'HH:mm', true);
            if (newTime.isValid()) {
                setDeliveryTime(newTime.seconds(0));
                return;
            }
        }
    };

    const handleDateDisplayChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value; // DD/MM/YYYY
        setDateDisplay(value);
        const parsed = moment(value, 'DD/MM/YYYY', true);
        if (parsed.isValid()) {
            setOrder(prev => ({
                ...prev,
                deliveryDate: parsed.format('YYYY-MM-DD')
            }));
        }
    };

    const handleDateDisplayBlur = () => {
        if (!dateDisplay) return;
        const parsed = moment(dateDisplay, 'DD/MM/YYYY', true);
        if (!parsed.isValid()) {
            // nếu không hợp lệ, khôi phục theo state hiện tại
            if (order.deliveryDate) {
                const d = moment(order.deliveryDate, 'YYYY-MM-DD');
                setDateDisplay(d.isValid() ? d.format('DD/MM/YYYY') : '');
            } else {
                setDateDisplay('');
            }
        } else {
            setDateDisplay(parsed.format('DD/MM/YYYY'));
        }
    };

    const handleTimeDisplayBlur = () => {
        if (!timeDisplay) return;
        const parsed = moment(timeDisplay, 'HH:mm', true);
        if (!parsed.isValid()) {
            if (deliveryTime) {
                setTimeDisplay(deliveryTime.format('HH:mm'));
            } else {
                const now = moment();
                setDeliveryTime(now);
                setTimeDisplay(now.format('HH:mm'));
            }
        } else {
            setTimeDisplay(parsed.format('HH:mm'));
        }
    };

    /**
     * Thiết lập tiền đặt cọc theo phần trăm của tổng giá trị (bao gồm phí ship)
     * @param percentage - Phần trăm (50 hoặc 100)
     */
    const setQuickDeposit = (percentage: number) => {
        const totalWithShipping = (order.totalValue || 0) + (order.shippingFee || 0);
        const depositAmount = Math.round(totalWithShipping * percentage / 100);
        setOrder(prev => ({
            ...prev,
            deposit: depositAmount
        }));
    };

    /**
     * Tạo DateTime kết hợp ngày giao hàng và thời gian giao hàng
     * @returns Chuỗi DateTime theo định dạng ISO 8601 (UTC)
     */
    const createDateTime = (): string => {
        if (!order.deliveryDate || !deliveryTime) {
            console.log('Thiếu ngày hoặc giờ, trả về ngày mặc định:', order.deliveryDate);
            return order.deliveryDate;
        }

        try {
            // Sử dụng utility function để tạo UTC datetime từ local date và time
            const timeString = deliveryTime.format('HH:mm');
            const utcDateTime = createUTCDatetime(order.deliveryDate, timeString);

            console.log('Tạo DateTime kết hợp:', {
                ngàyGốc: order.deliveryDate,
                giờPhút: timeString,
                kếtQuả: utcDateTime
            });

            return utcDateTime;
        } catch (error) {
            console.error('Lỗi khi tạo DateTime:', error);
            return order.deliveryDate;
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null); // Reset error message
        setSuccess(null); // Reset success message

        // Kiểm tra các trường bắt buộc
        if (!customerName) {
            setError('Vui lòng nhập tên khách hàng.');
            return;
        }

        if (!order.product) {
            setError('Vui lòng nhập tên sản phẩm.');
            return;
        }

        if (!order.deliveryDate) {
            setError('Vui lòng chọn ngày giao hàng.');
            return;
        }

        try {
            setSubmitting(true);

            // Tạo datetime từ deliveryDate và deliveryTime
            const finalDateTime = createDateTime();
            console.log('---------------------');
            console.log('THÔNG TIN GIAO HÀNG:');
            console.log('Ngày giao hàng (dạng chuỗi):', order.deliveryDate);
            console.log('Ngày giao hàng (dạng đối tượng):', moment(order.deliveryDate).format('YYYY-MM-DD'));
            console.log('Thời gian giao hàng:', deliveryTime ? deliveryTime.format('HH:mm:ss') : 'Không có');
            console.log('DateTime hoàn chỉnh để gửi đi:', finalDateTime);
            console.log('---------------------');

            let customerId: number | undefined = customer?.id;

            // Nếu có số điện thoại và tên khách hàng, tìm kiếm hoặc tạo khách hàng
            if (phoneNumber && customerName) {
                if (isNewCustomer || !customer) {
                    // Tạo khách hàng mới nếu có số điện thoại và tên
                    const newCustomer: Customer = {
                        phoneNumber: phoneNumber,
                        name: customerName,
                        address: order.address
                    };

                    try {
                        const customerResponse = await CustomerService.create(newCustomer);
                        if (customerResponse && customerResponse.data) {
                            customerId = customerResponse.data.id;
                        } else {
                            throw new Error('Không thể tạo khách hàng mới');
                        }
                    } catch (err) {
                        console.error('Lỗi tạo khách hàng mới:', err);
                    }
                } else if (customer && (customer.phoneNumber !== phoneNumber || customer.name !== customerName || customer.address !== order.address)) {
                    // Cập nhật thông tin khách hàng nếu đã có khách hàng nhưng thông tin thay đổi
                    const updatedCustomer: Customer = {
                        ...customer,
                        phoneNumber: phoneNumber,
                        name: customerName,
                        address: order.address
                    };

                    try {
                        await CustomerService.update(customer.id || 0, updatedCustomer);
                        customerId = customer.id;
                    } catch (err) {
                        console.error('Lỗi cập nhật khách hàng:', err);
                    }
                }
            }

            try {
                // Chuẩn bị dữ liệu đơn hàng
                const orderData: Order = {
                    product: order.product,
                    quantity: Number(order.quantity) || 1,
                    price: Number(order.price) || 0,
                    totalValue: Number(order.totalValue) || 0,
                    deposit: Number(order.deposit) || 0,
                    shippingFee: Number(order.shippingFee) || 0,
                    remainingAmount: order.remainingAmount,
                    deliveryDate: finalDateTime,
                    status: order.status,
                    address: order.address || '',
                    notes: order.notes || '',
                    remarks: order.remarks || '',
                    customerName: customerName
                };

                // Chỉ thêm customerId nếu có khách hàng
                if (customerId) {
                    orderData.customerId = customerId;
                }

                if (isEditMode) {
                    await OrderService.update(Number(id), orderData, imageFile || undefined);
                    setSuccess('Đơn hàng đã được cập nhật thành công.');
                    // Điều hướng về trang danh sách đơn hàng sau khi cập nhật thành công
                    setTimeout(() => {
                        navigate(`${userPrefix}/orders`);
                    }, 1500);
                } else {
                    await OrderService.create(orderData, imageFile || undefined);
                    navigate(`${userPrefix}/orders`);
                }
            } catch (err: any) {
                // Hiển thị thông báo lỗi chi tiết hơn
                const errorMessage = err.response?.data?.message || 'Không thể lưu đơn hàng. Vui lòng kiểm tra lại thông tin và thử lại.';
                setError(errorMessage);
                console.error('Lỗi lưu đơn hàng:', err);
                console.error('Chi tiết lỗi:', err.response?.data); // Log chi tiết lỗi
            }
        } catch (err) {
            setError('Có lỗi xảy ra khi xử lý đơn hàng. Vui lòng thử lại sau.');
            console.error(err);
        } finally {
            setSubmitting(false);
        }
    };

    // Hàm đặt lại form về giá trị ban đầu
    const resetForm = () => {
        if (isEditMode && originalOrder) {
            setOrder(originalOrder);
            setSuccess(null);
            setError(null);

            // Đặt lại deliveryTime nếu có
                        if (originalOrder.deliveryDate) {
                try {
                    const dateTime = moment(originalOrder.deliveryDate);
                    if (dateTime.isValid()) {
                        setDeliveryTime(dateTime);
                        setDateDisplay(dateTime.format('DD/MM/YYYY'));
                        setTimeDisplay(dateTime.format('HH:mm'));
                    }
                } catch (error) {
                    console.error('Lỗi khi đặt lại thời gian giao hàng:', error);
                }
            }
        }
    };

    // Hàm xử lý thay đổi cho các trường tiền tệ
    const handleFormattedValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;

        // Cập nhật giá trị định dạng trong state
        setFormattedValues(prev => ({
            ...prev,
            [name]: value
        }));

        // Chuyển đổi giá trị định dạng thành số và cập nhật vào order
        const numericValue = parseFormattedNumber(value);
        setOrder(prev => ({
            ...prev,
            [name]: numericValue
        }));
    };

    // Hàm xử lý khi trường tiền tệ bị blur (mất focus)
    const handleFormattedValueBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        const { name } = e.target;
        const value = order[name as keyof typeof order];

        // Định dạng lại giá trị khi blur
        if (typeof value === 'number') {
            setFormattedValues(prev => ({
                ...prev,
                [name]: formatNumberInput(value as number)
            }));
        }
    };

    if (loading) {
        return (
            <div className="center-screen">
                <div className="spinner-border" role="status" aria-label="loading"></div>
            </div>
        );
    }

    return (
        <div className="order-form">
            {/* Styles moved to App.css (OrderForm section) */}

            {error && (
                <div className="alert alert-danger mb-3">
                    <i className="fas fa-exclamation-circle me-2"></i>
                    {error}
                </div>
            )}

            {success && (
                <div className="alert alert-success mb-3">
                    <i className="fas fa-check-circle me-2"></i>
                    {success}
                </div>
            )}

            <form onSubmit={handleSubmit}>
                <div className="row">
                    {/* Thông tin khách hàng */}
                    <div className="col-md-6 mb-4">
                        <div className="card h-100">
                            <div className="card-header bg-primary text-white">
                                <i className="fas fa-user me-1"></i>
                                {" "} Thông tin khách hàng
                            </div>
                            <div className="card-body">
                                <div className="mb-3">
                                    <label htmlFor="customerName" className="form-label">
                                        Tên khách hàng <span className="text-danger">*</span>
                                    </label>
                                    <div className="input-group">
                                        <span className="input-group-text">
                                            <i className="fas fa-user"></i>
                                        </span>
                                        <input
                                            type="text"
                                            className="form-control"
                                            id="customerName"
                                            value={customerName}
                                            onChange={handleCustomerNameChange}
                                            placeholder="Nhập tên khách hàng"
                                            required
                                            style={isEditMode && order.customerName && !order.customerId ? {
                                                fontWeight: 'bold',
                                                borderColor: '#17a2b8'
                                            } : {}}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label htmlFor="phoneNumber" className="form-label">
                                        Số điện thoại
                                    </label>
                                    <div className="input-group">
                                        <span className="input-group-text">
                                            <i className="fas fa-phone"></i>
                                        </span>
                                        <input
                                            type="text"
                                            className="form-control"
                                            id="phoneNumber"
                                            value={phoneNumber}
                                            onChange={handlePhoneNumberChange}
                                            placeholder="Nhập số điện thoại khách hàng"
                                        />
                                    </div>
                                    {isEditMode && customer && (
                                        <small className="text-info mb-3">
                                           
                                        </small>
                                    )}
                                </div>


                            </div>
                        </div>
                    </div>

                    {/* Thông tin đơn hàng */}
                    <div className="col-md-6 mb-4">
                        <div className="card h-100">
                            <div className="card-header bg-primary text-white">
                                <i className="fas fa-shopping-bag me-1"></i>
                                {" "}Thông tin đơn hàng
                            </div>
                            <div className="card-body">
                                <div className="row g-3">
                                    <div className="col-md-12 mb-3">
                                        <label htmlFor="product" className="form-label">
                                            Sản phẩm <span className="text-danger">*</span>
                                        </label>
                                        <div className="input-group">
                                            <span className="input-group-text">
                                                <i className="fas fa-box"></i>
                                            </span>
                                            <input
                                                type="text"
                                                className="form-control"
                                                id="product"
                                                name="product"
                                                value={order.product}
                                                onChange={handleChange}
                                                required
                                                placeholder="Nhập tên sản phẩm"
                                            />
                                        </div>
                                    </div>

                                    <div className="col-6 mb-3" style={{ paddingLeft: '0rem', paddingRight: '0.5rem' }}>
                                        <label htmlFor="quantity" className="form-label">
                                            Số lượng
                                        </label>
                                        <div className="input-group">
                                            <span className="input-group-text">
                                                <i className="fas fa-hashtag"></i>
                                            </span>
                                            <input
                                                type="number"
                                                className="form-control"
                                                id="quantity"
                                                name="quantity"
                                                min="1"
                                                value={order.quantity}
                                                onChange={handleChange}
                                            />
                                        </div>
                                    </div>

                                    <div className="col-6 mb-3" style={{ paddingLeft: '0.5rem', paddingRight: '0rem' }}>
                                        <label htmlFor="price" className="form-label">
                                            Giá (VND)
                                        </label>
                                        <div className="input-group">
                                            <span className="input-group-text">
                                                <i className="fas fa-dollar-sign"></i>
                                            </span>
                                            <input
                                                type="text"
                                                className="form-control"
                                                id="price"
                                                name="price"
                                                min="0"
                                                value={formattedValues.price}
                                                onChange={handleFormattedValueChange}
                                                onBlur={handleFormattedValueBlur}
                                            />
                                        </div>
                                    </div>

                                    <div className="col-md-12">
                                        <label htmlFor="deliveryDate" className="form-label">
                                            Ngày giao hàng <span className="text-danger">*</span>
                                        </label>
                                        <div className="row g-2">
                                            <div className="col-7" style={{ paddingLeft: '0rem', paddingRight: '0.5rem' }}>
                                                <div className="input-group">
                                                    <span className="input-group-text">
                                                        <i className="fas fa-calendar"></i>
                                                    </span>
                                                    {isIOS ? (
                                                        <input
                                                            type="date"
                                                            className="form-control"
                                                            id="deliveryDate"
                                                            name="deliveryDate"
                                                            value={order.deliveryDate}
                                                            onChange={handleChange}
                                                            required
                                                        />
                                                    ) : (
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            id="deliveryDate"
                                                            name="deliveryDateDisplay"
                                                            placeholder="dd/mm/yyyy"
                                                            value={dateDisplay}
                                                            onChange={handleDateDisplayChange}
                                                            onBlur={handleDateDisplayBlur}
                                                            required
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                            <div className="col-5" style={{ paddingLeft: '0.5rem', paddingRight: '0rem' }}>
                                                <div className="input-group">
                                                    <span className="input-group-text">
                                                        <i className="fas fa-clock"></i>
                                                    </span>
                                                    {isIOS ? (
                                                        <input
                                                            type="time"
                                                            className="form-control"
                                                            value={deliveryTime ? deliveryTime.format('HH:mm') : ''}
                                                            onChange={handleTimeInputChange}
                                                            step={300}
                                                        />
                                                    ) : (
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            placeholder="HH:mm"
                                                            value={timeDisplay}
                                                            onChange={handleTimeInputChange}
                                                            onBlur={handleTimeDisplayBlur}
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Thông tin thanh toán */}
                    <div className="col-md-6 mb-4">
                        <div className="card h-100">
                            <div className="card-header bg-primary text-white">
                                <i className="fas fa-money-bill-wave me-1"></i>
                                {" "} Thông tin thanh toán
                            </div>
                            <div className="card-body">
                                <div className="row g-3">
                                    <div className="col-md-12 mb-3">
                                        <label htmlFor="totalValue" className="form-label">
                                            Tổng giá trị (VND)
                                        </label>
                                        <div className="input-group">
                                            <span className="input-group-text">
                                                <i className="fa-solid fa-money-bill-1-wave"></i>
                                            </span>
                                            <input
                                                type="text"
                                                className="form-control"
                                                id="totalValue"
                                                value={formattedValues.totalValue}
                                                readOnly
                                            />
                                        </div>
                                    </div>

                                    <div className="col-md-12 mb-3">
                                        <label htmlFor="shippingFee" className="form-label">
                                            Phí Ship (VND)
                                        </label>
                                        <div className="input-group">
                                            <span className="input-group-text">
                                                <i className="fas fa-truck"></i>
                                            </span>
                                            <input
                                                type="text"
                                                className="form-control"
                                                id="shippingFee"
                                                name="shippingFee"
                                                min="0"
                                                value={formattedValues.shippingFee}
                                                onChange={handleFormattedValueChange}
                                                onBlur={handleFormattedValueBlur}
                                            />
                                        </div>
                                    </div>

                                    <div className="col-md-12 mb-3">
                                        <label htmlFor="deposit" className="form-label">
                                            Tiền đặt cọc (VND)
                                        </label>
                                        <div className="input-group">
                                            <span className="input-group-text">
                                                <i className="fas fa-hand-holding-usd"></i>
                                            </span>
                                            <input
                                                type="text"
                                                className="form-control"
                                                id="deposit"
                                                name="deposit"
                                                min="0"
                                                value={formattedValues.deposit}
                                                onChange={handleFormattedValueChange}
                                                onBlur={handleFormattedValueBlur}
                                            />

                                            <div className="d-flex gap-1 ps-2">
                                                <button
                                                    type="button"
                                                    className="btn btn-primary rounded-2 text-center"
                                                    onClick={() => setQuickDeposit(50)}
                                                    style={{ borderRadius: '0px' }}
                                                >
                                                    50%
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-primary rounded-2 text-center"
                                                    onClick={() => setQuickDeposit(100)}
                                                    style={{
                                                        borderStartStartRadius: '0px',
                                                        borderStartEndRadius: '10px',
                                                        borderEndEndRadius: '10px',
                                                        borderEndStartRadius: '0px',
                                                        borderLeft: '1px solid #dee2e6'
                                                    }}
                                                >
                                                    100%
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="col-md-12">
                                        <label htmlFor="remainingAmount" className="form-label">
                                            Số tiền cần thanh toán (VND)
                                        </label>
                                        <div className="input-group">
                                            <span className="input-group-text">
                                                <i className="fas fa-wallet"></i>
                                            </span>
                                            <input
                                                type="text"
                                                className="form-control"
                                                id="remainingAmount"
                                                value={formattedValues.remainingAmount}
                                                readOnly
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Thông tin bổ sung */}
                    <div className="col-md-6 mb-4">
                        <div className="card h-100">
                            <div className="card-header bg-primary text-white">
                                <i className="fas fa-info-circle me-1"></i>
                                {" "}Thông tin bổ sung
                            </div>
                            <div className="card-body">
                                <div className="row g-3">
                                    <div className="col-md-12 mb-3">
                                        <label htmlFor="address" className="form-label">
                                            Địa chỉ
                                        </label>
                                        <div className="input-group">
                                            <span className="input-group-text">
                                                <i className="fas fa-map-marker-alt"></i>
                                            </span>
                                            <input
                                                type="text"
                                                className="form-control"
                                                id="address"
                                                name="address"
                                                value={order.address || ''}
                                                onChange={handleChange}
                                                placeholder="Nhập địa chỉ giao hàng"
                                            />
                                        </div>
                                    </div>

                                    <div className="col-md-12 mb-3">
                                        <label htmlFor="status" className="form-label">
                                            Trạng thái
                                        </label>
                                        <div className="input-group">
                                            <span className="input-group-text">
                                                <i className="fa-solid fa-spinner"></i>
                                            </span>
                                            <select
                                                className="form-select text-center"
                                                id="status"
                                                name="status"
                                                value={order.status}
                                                onChange={handleChange}
                                                style={{ width: '50%' }}
                                            >
                                                <option value="pending">Chờ xử lý</option>
                                                <option value="preparing">Đang xử lý</option>
                                                <option value="done">Sẵn sàng giao</option>
                                                <option value="completed">Hoàn thành</option>
                                                <option value="cancelled">Hủy đơn</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div className="col-md-12 mb-3">
                                        <label htmlFor="notes" className="form-label">
                                            Tag
                                        </label>
                                        <div className="input-group">
                                            <span className="input-group-text">
                                                <i className="fas fa-tags"></i>
                                            </span>
                                            <textarea
                                                className="form-control"
                                                id="notes"
                                                name="notes"
                                                rows={3}
                                                value={order.notes || ''}
                                                onChange={handleChange}
                                                placeholder="Nhập tag cho đơn hàng"
                                            ></textarea>
                                        </div>
                                    </div>

                                    <div className="col-md-12 mb-3">
                                        <label htmlFor="remarks" className="form-label">
                                            Lưu ý
                                        </label>
                                        <div className="input-group">
                                            <span className="input-group-text">
                                                <i className="fas fa-sticky-note"></i>
                                            </span>
                                            <textarea
                                                className="form-control"
                                                id="remarks"
                                                name="remarks"
                                                rows={3}
                                                value={order.remarks || ''}
                                                onChange={handleChange}
                                                placeholder="Nhập lưu ý cho đơn hàng"
                                            ></textarea>
                                        </div>
                                    </div>

                                    <div className="col-md-12">
                                        <label htmlFor="image" className="form-label">
                                            Hình ảnh
                                        </label>
                                        <div className="input-group">
                                            <span className="input-group-text">
                                                <i className="fas fa-image"></i>
                                            </span>
                                            <input
                                                type="file"
                                                className="form-control"
                                                id="image"
                                                accept="image/*"
                                                onChange={handleImageChange}
                                                style={{ height: 'calc(1.5em + .75rem + 15px)' }}
                                            />
                                        </div>
                                        {imagePreview && (
                                            <div className="mt-3 text-center">
                                                <div className="position-relative d-inline-block">
                                                    <img
                                                        src={imagePreview}
                                                        alt="Preview"
                                                        className="img-thumbnail"
                                                        style={{ maxWidth: '200px', maxHeight: '200px' }}
                                                    />
                                                    <button
                                                        type="button"
                                                        className="btn btn-danger btn-sm position-absolute top-0 end-0"
                                                        onClick={handleRemoveImage}
                                                        style={{
                                                            transform: 'translate(50%, -50%)',
                                                            borderRadius: '50%',
                                                            width: '30px',
                                                            height: '30px',
                                                            padding: '0',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center'
                                                        }}
                                                        title="Xóa hình ảnh"
                                                    >
                                                        <i className="fas fa-times" style={{ fontSize: '12px' }}></i>
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row">
                    <div
                        className="d-flex ps-3 order-buttons-container"
                        style={buttonContainerStyle}
                    >
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => navigate(`${userPrefix}/orders`)}
                            disabled={submitting}
                        // style={{ fontSize: '16px', fontWeight: '500' }}
                        >
                            <i className="fas fa-times me-2"></i> Hủy
                        </button>
                        <button
                            type="submit"
                            className="btn btn-success "
                            disabled={submitting || !customerName || !order.product || !order.deliveryDate}
                        // style={{ fontSize: '16px', fontWeight: '500' }}
                        >
                            {submitting ? (
                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                            ) : (
                                <>
                                    <i className="fas fa-save me-2" ></i>
                                    {isEditMode ? ' Cập nhật' : ' Lưu'}
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </form>
            <div style={{ height: '90px' }}></div>
        </div>
    );
};

export default OrderForm; 